import { DataType } from "#gen/common/v1/types_pb.js";
import type { PostalAddressJson } from "#gen/google/type/postal_address_pb.js";
import type { JsonValue } from "@bufbuild/protobuf";
import type { Component } from "svelte";
import type { HTMLInputAttributes } from "svelte/elements";

type InputType = "text" | "email" | "tel" | "date" | "number" | "url";

interface DataTypeInputAttributes {
	type?: InputType;
	autocomplete?: NonNullable<HTMLInputAttributes["autocomplete"]>;
	inputmode?: NonNullable<HTMLInputAttributes["inputmode"]>;
}

export type DataTypeInputValue = JsonValue | undefined;

export function emptyDataTypeInputValue(dataType: DataType): DataTypeInputValue {
	switch (dataType) {
		case DataType.ARRAY:
			return [];
		case DataType.NUMBER:
		case DataType.BOOLEAN:
			return undefined;
		case DataType.ADDRESS:
		case DataType.IMAGE:
			return undefined;
		default:
			return "";
	}
}

function trimmed(value: JsonValue): JsonValue {
	return typeof value === "string" ? value.trim() : value;
}

export function isEmptyDataTypeInputValue(value: DataTypeInputValue): boolean {
	if (value === undefined) {
		return true;
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return false;
	}
	if (Array.isArray(value)) {
		return value.every(row => trimmed(row) === "");
	}
	if (typeof value === "string") {
		return trimmed(value) === "";
	}
	return false;
}

export interface DataTypeInputProps extends DataTypeInputAttributes {
	value: DataTypeInputValue;
	defaultValue?: DataTypeInputValue;
	disabled?: boolean;
	invalid?: boolean;
	required?: boolean;
	fullPath?: string;
	oninput?: (e: Event) => void;
	onchange?: (e: Event) => void;
	onblur?: (e: FocusEvent) => void;
	oninvalid?: (e: Event) => void;
}

export function stringValueFromDataTypeValue(value: DataTypeInputValue): string | undefined {
	return typeof value === "string" ? value : undefined;
}

export interface InputControl {
	component: Component<DataTypeInputProps>;
	attrs?: DataTypeInputAttributes;
}

export function inputValueFromInputEvent(e: Event): DataTypeInputValue {
	if (e instanceof CustomEvent) {
		const { detail } = e;
		if (detail == null) {
			return undefined;
		}
		if (typeof detail === "string" || typeof detail === "boolean" || typeof detail === "number") {
			return detail;
		}
		if (Array.isArray(detail)) {
			return detail;
		}
		if (typeof detail === "object" && detail !== null) {
			return detail as PostalAddressJson;
		}
		if (detail instanceof Event) {
			e = detail;
		}
	}
	return (e.target as HTMLInputElement | null)?.value ?? "";
}
